import type { QueryClient } from "@tanstack/react-query";
import type { SourceListItem, SourceListResponse } from "./useSourceList";
import type { UploadSourcePayload, UploadSourceResponse } from "./useUploadSource";

/** Query key for the full-tree source list used by facet reference-table options. */
export function referenceTableSourceListQueryKey(notebookId: number) {
  return ["tars", "source", "list", notebookId, "", null, 0, 100, true] as const;
}

export function buildSourceListItemFromUpload(
  response: UploadSourceResponse,
  variables: UploadSourcePayload
): SourceListItem {
  return {
    id: response.id,
    name: response.name,
    parent_id: variables.parent_id ?? null,
    type: variables.type,
    s3_key: variables.s3_key,
    mime_type: variables.mime_type,
    size_bytes: variables.size_bytes,
    tags: variables.tags,
    more: {
      ...(variables.more ?? {}),
      ...((response.more as Record<string, unknown>) ?? {}),
    },
  };
}

function patchSourceListResponse(
  old: SourceListResponse | undefined,
  item: SourceListItem
): SourceListResponse | undefined {
  if (!old?.sources) return old;
  const idx = old.sources.findIndex((s) => s.id === item.id);
  const sources =
    idx === -1
      ? [...old.sources, item]
      : old.sources.map((s, i) => (i === idx ? { ...s, ...item } : s));
  return { ...old, sources, total: sources.length };
}

/** Optimistically merge a newly uploaded source into cached list payloads. */
export function upsertSourceListItemInCache(
  queryClient: QueryClient,
  notebookId: number,
  item: SourceListItem
) {
  queryClient.setQueriesData<SourceListResponse>(
    { queryKey: ["tars", "source", "list", notebookId] },
    (old) => patchSourceListResponse(old, item)
  );
}

export function invalidateReferenceTableSourceList(
  queryClient: QueryClient,
  notebookId: number
) {
  return queryClient.invalidateQueries({
    queryKey: referenceTableSourceListQueryKey(notebookId),
  });
}
